// Admin API Service - Real implementation with backend integration
import { apiService } from './api';

// ===== DASHBOARD TYPES =====

export interface UserMetrics {
  total_users: number;
  active_users_30d: number;
  new_users_30d: number;
  users_by_tier: {
    free: number;
    premium: number;
    enterprise: number;
  };
  registration_trend: Array<{
    date: string;
    count: number;
  }>;
}

export interface AssessmentMetrics {
  total_assessments: number;
  assessments_30d: number;
  assessments_by_status: {
    in_progress: number;
    completed: number;
    abandoned: number;
  };
  assessments_by_profile: Array<{
    profile_name: string;
    count: number;
    percentage: number;
  }>;
  completion_rate: number;
  average_completion_time_minutes: number;
}

export interface FeedbackMetrics {
  total_feedbacks: number;
  feedbacks_30d: number;
  average_rating: number;
  rating_distribution: Array<{
    rating: number;
    count: number;
    percentage: number;
  }>;
  feedback_sentiment: {
    positive: number;
    neutral: number;
    negative: number;
  };
  recent_feedbacks: Array<{
    id: string;
    user_name: string;
    rating: number;
    comment: string;
    created_at: string;
    assessment_type: string;
  }>;
}

export interface SystemMetrics {
  uptime_percentage: number;
  response_time_avg_ms: number;
  error_rate_percentage: number;
  database_health: "healthy" | "warning" | "critical";
  storage_usage_mb: number;
  active_sessions: number;
}

export interface DashboardSummary {
  users: UserMetrics;
  assessments: AssessmentMetrics;
  feedbacks: FeedbackMetrics;
  system: SystemMetrics;
  last_updated: string;
}

export interface UserSummary {
  id: string;
  name: string;
  email: string;
  subscription_tier: string;
  roles: string[];
  is_active: boolean;
  created_at: string;
  last_login_at: string;
  assessment_count: number;
  feedback_count: number;
}

export interface UserDetail extends UserSummary {
  recent_activity: ActivityLog[];
  assessments: AssessmentSummary[];
  feedbacks: FeedbackSummary[];
}

export interface ActivityLog {
  id: string;
  type: string;
  description: string;
  created_at: string;
  metadata?: Record<string, any>;
}

export interface AssessmentSummary {
  id: string;
  type: string;
  status: string;
  user_id: string;
  user_email: string;
  content_version: string;
  created_at: string;
  completed_at?: string;
  duration_minutes?: number;
  score_percentage?: number;
  primary_profile?: string;
}

export interface FeedbackSummary {
  id: string;
  user_id: string;
  user_email: string;
  assessment_id?: string;
  rating: number;
  comment: string;
  category: string;
  created_at: string;
  metadata?: Record<string, any>;
}

export interface AnalyticsSummary {
  total_users: number;
  active_users: number;
  new_users_today: number;
  new_users_this_week: number;
  new_users_this_month: number;
  total_assessments: number;
  completed_assessments: number;
  assessments_today: number;
  total_feedbacks: number;
  average_feedback_rating: number;
  users_by_tier: Record<string, number>;
  assessments_by_status: Record<string, number>;
  feedbacks_by_rating: Record<string, number>;
  top_communication_profiles: ProfileStatistic[];
  recent_activity: ActivityLog[];
  system_health: SystemHealth;
}

export interface ProfileStatistic {
  profile: string;
  count: number;
  percentage: number;
}

export interface SystemHealth {
  status: string;
  uptime: string;
  database_status: string;
  firebase_status: string;
  redis_status: string;
  memory_usage: ResourceUsage;
  cpu_usage: ResourceUsage;
  disk_usage: ResourceUsage;
  active_connections: number;
  requests_per_minute: number;
  error_rate: number;
  average_response_time: number;
  last_check: string;
  alerts?: SystemAlert[];
  version: string;
  environment: string;
}

export interface ResourceUsage {
  used: number;
  total: number;
  percentage: number;
}

export interface SystemAlert {
  level: string;
  message: string;
  source: string;
  created_at: string;
}

export interface UserFilters {
  search?: string;
  tier?: string;
  role?: string;
  active?: string;
}

export interface UpdateUserRequest {
  name?: string;
  email?: string;
  subscription_tier?: string;
  is_active?: boolean;
}

export interface PaginatedResponse<T> {
  data: T[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    total_pages: number;
  };
}

export interface QuickFeedbackSummary {
  total_count: number;
  count_by_score: Record<string, number>;
  last_submitted: string;
}

export interface DetailedFeedbackSummary {
	id: string;
	user_id: string;
	assessment_id: string;
	rating: number;
	comment: string;
	category: string;
	created_at: string;
	metadata: {
		average_accuracy_rating: number;
		average_overall_rating: number;
		average_satisfaction: number;
		recommendation_rate: number;
		section_ratings: Record<string, number>;
		total_feedbacks: number;
	};
}

export interface FeedbackAnalysis {
  total_feedbacks: number;
  average_overall_rating: number;
  average_accuracy_rating: number;
  average_satisfaction_rating: number;
  recommendation_rate: number;
  rating_distribution: Record<string, number>;
  sentiment: {
    positive: number;
    neutral: number;
    negative: number;
  };
  feedbacks_by_category: Record<string, number>;
  most_recent_feedback_at: string;
  recent_comments: RecentFeedbackComment[];
  section_ratings: Record<string, number>;
  common_improvement_areas: ImprovementAreaStat[];
  // Deprecated field for backward compatibility
  average_rating: number;
}

export interface RecentFeedbackComment {
  id: string;
  user_id?: string;
  comment: string;
  overall_rating: number;
  would_recommend: boolean;
  created_at: string;
}

export interface ImprovementAreaStat {
  area: string;
  count: number;
}

class AdminApiService {
  private baseUrl: string;

  constructor() {
    // Use relative URLs for consistency with other API services
    // This works with Vite proxy in development and Firebase hosting rewrites in production
    const API_BASE_URL = '/api/v2';
    this.baseUrl = `${API_BASE_URL}/admin`; // Real backend API
  }

  // ===== USER MANAGEMENT =====

  async getUsers(
    page: number = 1,
    limit: number = 20,
    filters: UserFilters = {}
  ): Promise<PaginatedResponse<UserSummary>> {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
      ...Object.fromEntries(Object.entries(filters).filter(([_, v]) => v !== undefined))
    });

    const response = await fetch(`${this.baseUrl}/users?${params}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to get users: ${response.statusText}`);
    }

    return response.json();
  }

  async getUser(userId: string): Promise<UserDetail> {
    const response = await fetch(`${this.baseUrl}/users/${userId}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to get user: ${response.statusText}`);
    }

    return response.json();
  }

  async updateUser(userId: string, data: UpdateUserRequest): Promise<void> {
    const response = await fetch(`${this.baseUrl}/users/${userId}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      throw new Error(`Failed to update user: ${response.statusText}`);
    }
  }

  async deactivateUser(userId: string): Promise<void> {
    const response = await fetch(`${this.baseUrl}/users/${userId}/deactivate`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to deactivate user: ${response.statusText}`);
    }
  }

  async activateUser(userId: string): Promise<void> {
    const response = await fetch(`${this.baseUrl}/users/${userId}/activate`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to activate user: ${response.statusText}`);
    }
  }

  async updateUserRoles(userId: string, roles: string[]): Promise<void> {
    const response = await fetch(`${this.baseUrl}/users/${userId}/roles`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
      },
      body: JSON.stringify({ roles }),
    });

    if (!response.ok) {
      throw new Error(`Failed to update user roles: ${response.statusText}`);
    }
  }

  // ===== ANALYTICS =====

  async getAnalyticsSummary(): Promise<{ summary: AnalyticsSummary }> {
    const response = await fetch(`${this.baseUrl}/analytics/summary`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to get analytics summary: ${response.statusText}`);
    }

    return response.json();
  }

  // ===== DASHBOARD APIS =====

  async getDashboardSummary(): Promise<DashboardSummary> {
    const response = await fetch(`${this.baseUrl}/dashboard/summary`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to get dashboard summary: ${response.statusText}`);
    }

    return response.json();
  }

  async getDashboardUserMetrics(): Promise<UserMetrics> {
    const response = await fetch(`${this.baseUrl}/dashboard/users`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to get user metrics: ${response.statusText}`);
    }

    return response.json();
  }

  async getDashboardAssessmentMetrics(): Promise<AssessmentMetrics> {
    const response = await fetch(`${this.baseUrl}/dashboard/assessments`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to get assessment metrics: ${response.statusText}`);
    }

    return response.json();
  }

  async getDashboardFeedbackMetrics(): Promise<FeedbackMetrics> {
    const response = await fetch(`${this.baseUrl}/dashboard/feedbacks`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to get feedback metrics: ${response.statusText}`);
    }

    return response.json();
  }

  async getDashboardSystemMetrics(): Promise<SystemMetrics> {
    const response = await fetch(`${this.baseUrl}/dashboard/system`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to get system metrics: ${response.statusText}`);
    }

    return response.json();
  }

  async getUserAnalytics(period: string = '30d'): Promise<any> {
    const response = await fetch(`${this.baseUrl}/analytics/users?period=${period}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to get user analytics: ${response.statusText}`);
    }

    return response.json();
  }

  async getAssessmentAnalytics(period: string = '30d'): Promise<any> {
    const response = await fetch(`${this.baseUrl}/analytics/assessments?period=${period}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to get assessment analytics: ${response.statusText}`);
    }

    return response.json();
  }

  async getFeedbackAnalytics(period: string = '30d'): Promise<any> {
    const response = await fetch(`${this.baseUrl}/analytics/feedbacks?period=${period}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to get feedback analytics: ${response.statusText}`);
    }

    return response.json();
  }

  async getRevenueAnalytics(period: string = '30d'): Promise<any> {
    const response = await fetch(`${this.baseUrl}/analytics/revenue?period=${period}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to get revenue analytics: ${response.statusText}`);
    }

    return response.json();
  }

  // ===== ASSESSMENT MANAGEMENT =====

  async getAllAssessments(
    page: number = 1,
    limit: number = 20,
    userId?: string,
    status?: string
  ): Promise<PaginatedResponse<AssessmentSummary>> {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    });
    
    if (userId) params.append('user_id', userId);
    if (status) params.append('status', status);

    const response = await fetch(`${this.baseUrl}/assessments?${params}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to get assessments: ${response.statusText}`);
    }

    return response.json();
  }

  async getAssessment(assessmentId: string): Promise<AssessmentSummary> {
    const response = await fetch(`${this.baseUrl}/assessments/${assessmentId}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to get assessment: ${response.statusText}`);
    }

    return response.json();
  }

  async deleteAssessment(assessmentId: string): Promise<void> {
    const response = await fetch(`${this.baseUrl}/assessments/${assessmentId}`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to delete assessment: ${response.statusText}`);
    }
  }

  // ===== FEEDBACK MANAGEMENT =====

  async getAllFeedbacks(
    page: number = 1,
    limit: number = 20,
    userId?: string,
    rating?: string
  ): Promise<PaginatedResponse<FeedbackSummary>> {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    });
    
    if (userId) params.append('user_id', userId);
    if (rating) params.append('rating', rating);

    const response = await fetch(`${this.baseUrl}/feedbacks?${params}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to get feedbacks: ${response.statusText}`);
    }

    return response.json();
  }

  async getFeedback(feedbackId: string): Promise<FeedbackSummary> {
    const response = await fetch(`${this.baseUrl}/feedbacks/${feedbackId}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to get feedback: ${response.statusText}`);
    }

    return response.json();
  }

  async deleteFeedback(feedbackId: string): Promise<void> {
    const response = await fetch(`${this.baseUrl}/feedbacks/${feedbackId}`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to delete feedback: ${response.statusText}`);
    }
  }

  async getFeedbackSummary(): Promise<{ summary: DetailedFeedbackSummary }> {
    const response = await fetch(`${this.baseUrl}/feedbacks/summary`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to get feedback summary: ${response.statusText}`);
    }

    return response.json();
  }

  async getQuickFeedbackSummary(): Promise<{ summary: QuickFeedbackSummary }> {
    const response = await fetch(`${this.baseUrl}/feedbacks/quick/summary`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
      },
    });

    if (!response.ok) {
      throw new Error('Failed to get quick feedback summary');
    }
    return response.json();
  }

  async getFeedbackAnalysis(): Promise<{ analysis: FeedbackAnalysis }> {
    const response = await fetch(`${this.baseUrl}/feedbacks/analysis`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to get feedback analysis: ${response.statusText}`);
    }

    return response.json();
  }

  // ===== SYSTEM MANAGEMENT =====

  async getSystemHealth(): Promise<SystemHealth> {
    const response = await fetch(`${this.baseUrl}/system/health`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to get system health: ${response.statusText}`);
    }

    return response.json();
  }

  async getSystemMetrics(): Promise<any> {
    const response = await fetch(`${this.baseUrl}/system/metrics`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to get system metrics: ${response.statusText}`);
    }

    return response.json();
  }

  async setMaintenanceMode(enabled: boolean, message?: string): Promise<void> {
    const response = await fetch(`${this.baseUrl}/system/maintenance`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
      },
      body: JSON.stringify({ enabled, message }),
    });

    if (!response.ok) {
      throw new Error(`Failed to set maintenance mode: ${response.statusText}`);
    }
  }
}

export const adminApi = new AdminApiService();